import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Paper,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import BookingKPIWidget from "../../analytics/components/BookingKPIWidget";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";

const roleConfig = {
  "tour-owner": {
    label: "Tour Owner",
    icon: <StorefrontIcon sx={{ fontSize: 18 }} />,
    color: "#E50000",
    bg: "linear-gradient(135deg, #E50000 15%, #f58484 100%)",
  },
  "tour-distributor": {
    label: "Distributor",
    icon: <TravelExploreIcon sx={{ fontSize: 18 }} />,
    color: "#3B82F6",
    bg: "linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)",
  },
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const ShareableLinkCard = ({ userId }) => {
  const publicUrl = `${window.location.origin}/d/${userId}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for browsers that block clipboard without HTTPS
      const el = document.createElement("textarea");
      el.value = publicUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "white",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <LinkIcon sx={{ color: "#E50000", fontSize: 20 }} />
        <Typography fontWeight={700} fontSize="0.95rem">
          Your Public Booking Page
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Share this link with customers or embed it as a QR code. Anyone with the
        link can browse your tours and book directly.
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        spacing={1}
      >
        <Box
          sx={{
            flex: 1,
            bgcolor: "#F7F8FC",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 1.5,
            py: 1,
            fontFamily: "monospace",
            fontSize: "0.82rem",
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            userSelect: "all",
          }}
        >
          {publicUrl}
        </Box>

        <Stack direction="row" spacing={1} flexShrink={0}>
          <Tooltip title={copied ? "Copied!" : "Copy link"}>
            <Button
              variant="outlined"
              size="small"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.8rem",
                color: copied ? "success.main" : "text.primary",
                borderColor: copied ? "success.main" : "divider",
                "&:hover": { borderColor: "#E50000", color: "#E50000" },
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            disableElevation
            endIcon={<OpenInNewIcon />}
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              fontSize: "0.8rem",
              bgcolor: "#E50000",
              "&:hover": { bgcolor: "#C00000" },
            }}
          >
            View Page
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const EmbedCodeCard = ({ userId }) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || "";
  const widgetUrl = process.env.REACT_APP_WIDGET_URL || "";
  const embedCode = `<script\n  src="${widgetUrl}"\n  data-distributor="${userId}"\n  data-api-url="${apiUrl}">\n</script>`;

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
    } catch {
      const el = document.createElement("textarea");
      el.value = embedCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "white",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <CodeIcon sx={{ color: "#3B82F6", fontSize: 20 }} />
        <Typography fontWeight={700} fontSize="0.95rem">
          Get Embed Code
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Paste this snippet anywhere on your website to display your tour
        listings inline.
      </Typography>

      <Box
        sx={{
          bgcolor: "#0f172a",
          borderRadius: 2,
          p: 2,
          fontFamily: "monospace",
          fontSize: "0.8rem",
          color: "#e2e8f0",
          whiteSpace: "pre",
          overflowX: "auto",
          lineHeight: 1.7,
          mb: 1.5,
          userSelect: "all",
        }}
      >
        {embedCode}
      </Box>

      <Stack direction="row" spacing={1}>
        <Tooltip title={copied ? "Copied!" : "Copy snippet"}>
          <Button
            variant="outlined"
            size="small"
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              fontSize: "0.8rem",
              color: copied ? "success.main" : "text.primary",
              borderColor: copied ? "success.main" : "divider",
              "&:hover": { borderColor: "#3B82F6", color: "#3B82F6" },
            }}
          >
            {copied ? "Copied!" : "Copy Code"}
          </Button>
        </Tooltip>
      </Stack>

      <Divider sx={{ mt: 2, mb: 1.5 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.6 }}
      >
        The widget fetches your tours automatically using your distributor ID.
        Visitors can click <strong>Book Now</strong> on any card to go straight
        to the booking page.
      </Typography>
    </Paper>
  );
};

const Dashboard = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("authUser"));
    if (storedUser) setUser(storedUser);
  }, []);

  const config = user?.role ? roleConfig[user.role] : null;

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 }, bgcolor: "#F7F8FC" }}>
      {user && (
        <>
          {/* Welcome banner */}
          <Box
            sx={{
              mb: 3,
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3,
              background:
                config?.bg ??
                "linear-gradient(135deg, #E50000 0%, #B80000 100%)",
              color: "#FFFFFF",
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                top: -40,
                right: -40,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                pointerEvents: "none",
              },
              "&::before": {
                content: '""',
                position: "absolute",
                bottom: -60,
                right: 60,
                width: 240,
                height: 240,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
                pointerEvents: "none",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#FFFFFF", letterSpacing: "-0.01em" }}
              >
                {getGreeting()}, {user.firstName}!
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.75)", mb: 1.5 }}
            >
              Here's an overview of your bookings and activity.
            </Typography>
            {config && (
              <Chip
                icon={React.cloneElement(config.icon, {
                  sx: {
                    color: "rgba(255,255,255,0.9) !important",
                    fontSize: 15,
                  },
                })}
                label={config.label}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  border: "1px solid rgba(255,255,255,0.3)",
                  backdropFilter: "blur(4px)",
                }}
              />
            )}
          </Box>

          {/* Shareable link + embed code — distributors only */}
          {user.role === "tour-distributor" && (
            <>
              <ShareableLinkCard userId={user._id} />
              <EmbedCodeCard userId={user._id} />
            </>
          )}

          {/* KPI widgets */}
          {user.role === "tour-owner" && (
            <BookingKPIWidget ownerId={user._id} />
          )}
          {user.role === "tour-distributor" && (
            <BookingKPIWidget distributorId={user._id} />
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
